import { useQueryClient } from "@tanstack/react-query";
import Pusher, { Channel } from "pusher-js";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";

import {
  getReportsByOrderId,
  useFeatureflags,
  useOrdersCache,
  useSessionUser,
} from "@web-app/api";
import config from "@web-app/config";
import {
  toSerializableOrder,
  toSerializableRoute,
} from "@web-app/rendering/toSerializable";
import { workerBridge } from "@web-app/rendering/workerBridge";
import useDispatchStore from "@web-app/store/dispatchStore";
import {
  AppFilterType,
  DriverEntity,
  Order,
  Report,
  Route,
} from "@web-app/types";
import { createTodaysDateInTimezone } from "@web-app/utils/helpers";

export default function useDispatchPage() {
  const [isBulkImportModalOpen, setIsBulkImportModalOpen] = useState(false);
  const [selectWithPolygon, setSelectWithPolygon] = useState(false);
  const { routeModalMode, focusedRouteId, appFilter, dateUpdateAt } =
    useDispatchStore();
  const { data: user } = useSessionUser();
  const { hasFeature } = useFeatureflags();
  const queryClient = useQueryClient();
  const userTimezone = user?.timezone;
  const ordersCache = useOrdersCache();
  const pusherRef = useRef<Pusher | null>(null);
  const channelsRef = useRef<{ assignees: Channel; workspace: Channel } | null>(
    null,
  );
  const togglePolygonSelection = () => {
    setSelectWithPolygon(!selectWithPolygon);
  };

  useEffect(() => {
    if (user && userTimezone) {
      const { startDateString, endDateString } =
        createTodaysDateInTimezone(userTimezone);

      const shouldResetToToday =
        !dateUpdateAt || (dateUpdateAt && dateUpdateAt !== startDateString);

      if (shouldResetToToday) {
        useDispatchStore.setState({
          appFilter: {
            ...appFilter,
            scheduledDateTimeEnd: endDateString,
            scheduledDateTimeStart: startDateString,
          },
          dateUpdateAt: startDateString,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, userTimezone, dateUpdateAt]);

  useEffect(() => {
    if (!user) {
      return;
    }

    if (pusherRef.current) {
      return;
    }

    const pusher = new Pusher(config.SOKETI_APP_KEY, {
      cluster: "",
      enabledTransports: ["ws", "wss"],
      forceTLS: false,
      wsHost: config.SOKETI_HOST,
      wsPort: parseInt(config.SOKETI_PORT),
      wssPort: parseInt(config.SOKETI_PORT),
    });

    pusher.connection.bind("error", () => {
      console.error("Soketi - Something went wrong!");
    });

    pusher.connection.bind("connected", () => {
      console.info("Soketi - Connected!");
    });

    pusherRef.current = pusher;

    return () => {
      if (pusherRef.current) {
        pusherRef.current.disconnect();
        pusherRef.current = null;
      }
    };
  }, [user]);

  useEffect(() => {
    if (!user || !pusherRef.current) {
      return;
    }

    const pusher = pusherRef.current;
    const assigneesChannel = pusher.subscribe("drivers");
    const workspaceChannelName = "internal-api@" + user.currentWorkspaceId;
    const workspaceChannel = pusher.subscribe(workspaceChannelName);

    channelsRef.current = {
      assignees: assigneesChannel,
      workspace: workspaceChannel,
    };

    workspaceChannel.bind("orderUpdated", (updatedOrder: Order) => {
      // web-api sends this after writing the order's timeline events, so an
      // open timeline picks up changes like checklist items being checked.
      queryClient.invalidateQueries({
        queryKey: ["orders-timeline", { orderId: updatedOrder.id }],
      });
      ordersCache.updateCachedOrders((data) => {
        const existing = data.orders.get(updatedOrder.id);
        if (!existing) return data;

        workerBridge.upsertOrders([toSerializableOrder(updatedOrder)]);

        const updatedOrders = new Map(data.orders);
        updatedOrders.set(updatedOrder.id, { ...existing, ...updatedOrder });
        return {
          apps: data.apps,
          assignees: data.assignees,
          orders: updatedOrders,
        };
      });
    });

    workspaceChannel.bind(
      "bulkOrderCreated",
      (data: { status: boolean; totalCount: number }) => {
        if (data.status) {
          ordersCache.invalidateOrdersCache();
          toast.success("Orders list updated");
        }
      },
    );

    workspaceChannel.bind(
      "bulkroute:created",
      (data: { routeIds: string[] }) => {
        ordersCache.invalidateOrdersCache();
        toast.success(
          `Successfully created ${data.routeIds.length} routes! Orders updated.`,
        );
      },
    );

    assigneesChannel.bind(
      "driver:updated",
      (data: {
        dutyStatus: DriverEntity["dutyStatus"];
        userProfileId: string;
      }) => {
        queryClient.setQueryData<Map<string, DriverEntity>>(
          ["drivers"],
          (drivers) => {
            if (drivers) {
              const clonedDrivers = structuredClone(drivers);
              const driver = clonedDrivers?.get(data.userProfileId);
              if (driver) {
                const updatedAssignee = {
                  ...driver,
                  dutyStatus: data.dutyStatus,
                };
                clonedDrivers.set(data.userProfileId, updatedAssignee);
                return clonedDrivers;
              }
            }
            return drivers;
          },
        );
      },
    );

    workspaceChannel.bind("routeUpdated", (updatedRoute: Route) => {
      ordersCache.updateCachedOrders((data) => {
        const hasOrdersOnRoute = Array.from(data.orders.values()).some(
          (order) => order.routeId === updatedRoute.id,
        );
        if (!hasOrdersOnRoute) return data;

        workerBridge.upsertRoutes([toSerializableRoute(updatedRoute)]);

        const updatedOrders = new Map(data.orders);
        updatedOrders.forEach((order, id) => {
          if (order.routeId === updatedRoute.id) {
            updatedOrders.set(id, { ...order, route: updatedRoute });
          }
        });
        return {
          apps: data.apps,
          assignees: data.assignees,
          orders: updatedOrders,
        };
      });
    });

    workspaceChannel.bind("routeCreated", () => {
      ordersCache.invalidateOrdersCache();
    });

    workspaceChannel.bind(
      "expediteQuoteAccepted",
      (data: { entity: string; entityId: string; status: string }) => {
        ordersCache.invalidateOrdersCache();
        toast.success(
          `Expedite quotation accepted for ${data.entity === "order" ? "order" : "route"}`,
        );
      },
    );

    workspaceChannel.bind(
      "orderMetadataUpdated",
      (data: { orderId: string }) => {
        queryClient.invalidateQueries({
          queryKey: ["order-metadata", data.orderId],
        });
      },
    );

    workspaceChannel.bind(
      "reportCreated",
      async (data: {
        createdAt: string;
        orderId: string;
        reportId: string;
        type: string;
      }) => {
        queryClient.invalidateQueries({
          queryKey: ["order-reports", data.orderId],
        });

        const batchQueries = queryClient
          .getQueryCache()
          .findAll({ queryKey: ["batch-reports"] });

        for (const query of batchQueries) {
          const queryKey = query.queryKey as string[];
          if (queryKey.includes(data.orderId)) {
            try {
              const updatedReports = await getReportsByOrderId(data.orderId);

              queryClient.setQueryData(
                queryKey,
                (oldData: Record<string, Report[]>) => {
                  if (oldData) {
                    return {
                      ...oldData,
                      [data.orderId]: updatedReports,
                    };
                  }
                  return oldData;
                },
              );
            } catch {
              queryClient.invalidateQueries({ queryKey });
            }
          }
        }
      },
    );

    const invalidateAutoAssignSearching = () => {
      queryClient.invalidateQueries({
        queryKey: ["auto-assign-searching"],
      });
    };
    workspaceChannel.bind("auto-assign:queued", invalidateAutoAssignSearching);
    workspaceChannel.bind(
      "auto-assign:searching",
      invalidateAutoAssignSearching,
    );
    workspaceChannel.bind(
      "auto-assign:accepted",
      invalidateAutoAssignSearching,
    );
    workspaceChannel.bind(
      "auto-assign:exhausted",
      invalidateAutoAssignSearching,
    );
    workspaceChannel.bind("auto-assign:expired", invalidateAutoAssignSearching);
    workspaceChannel.bind(
      "auto-assign:declined",
      invalidateAutoAssignSearching,
    );

    return () => {
      if (channelsRef.current) {
        channelsRef.current.workspace.unsubscribe();
        channelsRef.current.assignees.unsubscribe();
        channelsRef.current = null;
      }
    };
  }, [appFilter, queryClient, user, userTimezone]);

  const setAppFilter = (appFilter: AppFilterType) => {
    useDispatchStore.setState({
      appFilter,
      focusedOrderId: null,
      focusedRouteId: null,
      mapFocusedOrderId: null,
      mapFocusedRouteId: null,
    });
  };

  return {
    appFilter,
    focusedRouteId,
    hasFeature,
    isBulkImportModalOpen,
    routeModalMode,
    selectWithPolygon,
    setAppFilter,
    setIsBulkImportModalOpen,
    togglePolygonSelection,
    user,
  };
}
